import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Truck, Shield, Star } from 'lucide-react';
import apiInstance from '../../utils/axios';


const HeroSection = () => {
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await apiInstance.get('/carousel/');
        // Handle both direct array and paginated response
        const carouselData = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setCarouselImages(carouselData);
      } catch (error) {
        console.error('Error fetching carousel images:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselImages();
  }, []);

  useEffect(() => {
    if (carouselImages.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [carouselImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  if (loading) {
    return (
      <div style={{
        height: '600px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '18px',
      }}>
        Cargando...
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative',
      height: isMobile ? '280px' : '600px',
      overflow: 'hidden',
      background: carouselImages.length === 0 
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'transparent',
    }}>
      {/* Background Carousel */}
      {carouselImages.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}>
          {carouselImages.map((image, index) => (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: index === currentSlide ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
              }}
            >
              <img
                src={image.image}
                alt={image.caption || `Hero ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0, 0, 0, 0.4)',
              }} />
            </div>
          ))}
        </div>
      )}

      {/* Content Overlay */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        textAlign: 'center',
        padding: '0 20px',
      }}>
        <div style={{
          maxWidth: '800px',
          width: '100%',
        }}>
          <h1 style={{
            fontSize: isMobile ? '1.3rem' : 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: '700',
            marginBottom: isMobile ? '8px' : '24px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            lineHeight: '1.2',
          }}>
            Descubrí productos increíbles
          </h1>
          <p style={{
            fontSize: isMobile ? '0.8rem' : 'clamp(1.1rem, 2vw, 1.5rem)',
            marginBottom: isMobile ? '12px' : '32px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            lineHeight: '1.4',
            opacity: 0.95,
            display: isMobile ? 'none' : 'block',
          }}>
            Comprá las últimas tendencias y ofertas exclusivas en un solo lugar.
          </p>
          <div style={{
            display: 'flex',
            gap: isMobile ? '8px' : '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <Link
              to="/products"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#3483fa',
                color: 'white',
                padding: isMobile ? '10px 16px' : '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: isMobile ? '13px' : '18px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(52, 131, 250, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2968d7';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(52, 131, 250, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3483fa';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(52, 131, 250, 0.3)';
              }}
            >
              <ShoppingBag size={isMobile ? 16 : 20} />
              Comprar ahora
            </Link>
            <Link
              to="/categories"
              style={{
                display: isMobile ? 'none' : 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'transparent',
                color: 'white',
                padding: isMobile ? '10px 16px' : '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: isMobile ? '13px' : '18px',
                fontWeight: '600',
                border: '2px solid white',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = '#333';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Ver categorías
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Hidden on mobile */}
      {carouselImages.length > 1 && !isMobile && (
        <>
          <button
            onClick={prevSlide}
            className="hero-carousel-arrow hero-carousel-prev"
            style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              e.target.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            <span style={{ fontSize: '24px', color: '#333' }}>‹</span>
          </button>
          <button
            onClick={nextSlide}
            className="hero-carousel-arrow hero-carousel-next"
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              e.target.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            <span style={{ fontSize: '24px', color: '#333' }}>›</span>
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {carouselImages.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          display: 'flex',
          gap: '8px',
        }}>
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: index === currentSlide ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>
      )}

      {/* Trust Indicators - Simplified on mobile */}
      <div style={{
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        zIndex: 2,
        background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
        padding: isMobile ? '12px 0 6px' : '20px 0 10px',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          gap: isMobile ? '16px' : '40px',
          flexWrap: 'wrap',
          padding: '0 10px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '8px',
            color: 'white',
            fontSize: isMobile ? '11px' : '14px',
          }}>
            <Truck size={isMobile ? 14 : 20} />
            <span>{isMobile ? 'Envío gratis' : 'Envío gratis a todo el país'}</span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '8px',
            color: 'white',
            fontSize: isMobile ? '11px' : '14px',
          }}>
            <Shield size={isMobile ? 14 : 20} />
            <span>Pago seguro</span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '8px',
            color: 'white',
            fontSize: isMobile ? '11px' : '14px',
          }}>
            <Star size={isMobile ? 14 : 20} />
            <span>{isMobile ? 'Calidad' : 'Máxima calidad'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;

